'use client';

import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { withDisplayName } from '../../lib/display-name';
import { Z_INDEX } from '../../lib/z-index';
import { SIcon } from '../SIcon';

export interface SDraggableItemRenderState {
  hovered: boolean;
  dragging: boolean;
  disabled: boolean;
}

export type SDraggableItemRenderProp = (state: SDraggableItemRenderState) => ReactNode;
export type SDraggableItemSlot = ReactNode | SDraggableItemRenderProp;
export type SDraggableItemSupportingTextPosition = 'right' | 'bottom';
export type SDraggableItemSize = 'sm' | 'md';

interface DragOverlayState {
  offsetX: number;
  offsetY: number;
  rect: DOMRect;
  x: number;
  y: number;
}

export interface SDraggableItemProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'title'
> {
  /** 드래그 아이템 제목 */
  title: SDraggableItemSlot;
  /** 제목을 보조하는 텍스트 */
  supportingText?: SDraggableItemSlot;
  /** 보조 텍스트 위치 */
  supportingTextPosition?: SDraggableItemSupportingTextPosition;
  /** 타이틀 뒤에 표시할 태그/콘텐츠 */
  trailing?: SDraggableItemSlot;
  /** 아이템 왼쪽 accent stripe 표시 여부 */
  accentStripe?: boolean;
  /** 외곽 테두리 사용 여부 */
  bordered?: boolean;
  /** 선택 상태 여부 */
  selected?: boolean;
  /** 드래그 중인 스타일을 고정해서 표시할지 여부 */
  dragging?: boolean;
  /** 조밀한 높이 사용 여부 */
  dense?: boolean;
  /** 타이포그래피·세로 패딩 크기 */
  size?: SDraggableItemSize;
  /** 비활성 상태 여부 */
  disabled?: boolean;
  /** 드래그 시 마우스를 따라가는 overlay 표시 여부 */
  dragOverlay?: boolean;
  /** 드래그 overlay 투명도 */
  dragOverlayOpacity?: number;
  /** 드래그 핸들에 전달할 속성 */
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
  /** 드래그 핸들 mouse down 이벤트 */
  onDragHandleMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
}

const setRefs = <T,>(refs: Array<Ref<T> | undefined>, value: T | null) => {
  refs.forEach(ref => {
    if (typeof ref === 'function') {
      ref(value);
      return;
    }

    if (ref) {
      ref.current = value;
    }
  });
};

function renderSlot(slot: SDraggableItemSlot | undefined, state: SDraggableItemRenderState) {
  return typeof slot === 'function' ? slot(state) : slot;
}

interface SDraggableItemViewProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'title'
> {
  title: SDraggableItemSlot;
  supportingText?: SDraggableItemSlot;
  supportingTextPosition: SDraggableItemSupportingTextPosition;
  trailing?: SDraggableItemSlot;
  accentStripe: boolean;
  bordered: boolean;
  selected: boolean;
  dense: boolean;
  size: SDraggableItemSize;
  dragging: boolean;
  disabled: boolean;
  hovered: boolean;
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
  onDragHandleMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
}

const SDraggableItemView = forwardRef<HTMLDivElement, SDraggableItemViewProps>(
  function SDraggableItemView(
    {
      title,
      supportingText,
      supportingTextPosition,
      trailing,
      accentStripe,
      bordered,
      selected,
      dense,
      size,
      dragging,
      disabled,
      hovered,
      dragHandleProps,
      onDragHandleMouseDown,
      className,
      ...rest
    },
    ref,
  ) {
    const renderState = { hovered, dragging, disabled };
    const titleNode = renderSlot(title, renderState);
    const supportingTextNode = renderSlot(supportingText, renderState);
    const hasSupportingText = supportingTextNode != null && supportingTextNode !== '';
    const trailingNode = renderSlot(trailing, renderState);
    const titleTypo =
      size === 'sm'
        ? selected
          ? 'typo-item-sm-selected'
          : 'typo-item-sm-default'
        : selected
          ? 'typo-item-md-selected'
          : 'typo-item-md-default';
    const paddingYClass = dense
      ? size === 'sm'
        ? 'py-(--cmp-listDraggableItem-paddingY-sm-dense)'
        : 'py-(--cmp-listDraggableItem-paddingY-md-dense)'
      : size === 'sm'
        ? 'py-(--cmp-listDraggableItem-paddingY-sm-normal)'
        : 'py-(--cmp-listDraggableItem-paddingY-md-normal)';

    return (
      <div
        ref={ref}
        aria-disabled={disabled || undefined}
        data-dragging={dragging || undefined}
        className={cn(
          'relative flex w-full items-center gap-(--cmp-listDraggableItem-gap) overflow-hidden px-(--cmp-listDraggableItem-paddingX)',
          titleTypo,
          disabled
            ? 'bg-(--cmp-listDraggableItem-bg-default) text-(--sys-color-disabled-text)'
            : selected
              ? 'bg-(--cmp-listDraggableItem-bg-selected) text-(--cmp-listDraggableItem-text-selected)'
              : 'bg-(--cmp-listDraggableItem-bg-default) text-(--cmp-listDraggableItem-text-default)',
          !disabled && !selected && !dragging && 'hover:bg-(--cmp-listDraggableItem-bg-hover)',
          hasSupportingText && supportingTextPosition === 'bottom'
            ? dense
              ? 'min-h-[52px]'
              : 'min-h-[60px]'
            : dense
              ? 'h-[32px]'
              : 'h-[40px]',
          paddingYClass,
          bordered
            ? cn(
                'rounded-(--cmp-listDraggableItem-radius-bordered) border border-(--cmp-listDraggableItem-border-default)',
                !disabled &&
                  !selected &&
                  !dragging &&
                  'hover:border-(--cmp-listDraggableItem-bordered-border-hover)',
                !disabled &&
                  selected &&
                  !dragging &&
                  'border-(--cmp-listDraggableItem-bordered-border-selected) bg-(--cmp-listDraggableItem-bordered-bg-selected)',
                !disabled &&
                  selected &&
                  dragging &&
                  'border-[length:var(--cmp-listDraggableItem-bordered-dragging-border-width)] border-(--cmp-listDraggableItem-bordered-dragging-border-color) bg-(--cmp-listDraggableItem-bordered-dragging-bg)',
              )
            : 'border-b border-(--cmp-listDraggableItem-border-default)',
          disabled && 'cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        {accentStripe && selected && !bordered && !disabled && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-1px] left-0 top-0 w-(--cmp-listDraggableItem-accentStripe-width) bg-(--cmp-listDraggableItem-accentStripe-color)"
          />
        )}
        <div
          {...dragHandleProps}
          role={dragHandleProps?.role ?? 'button'}
          tabIndex={dragHandleProps?.tabIndex ?? (disabled ? -1 : 0)}
          aria-label={dragHandleProps?.['aria-label'] ?? '드래그 핸들'}
          aria-disabled={disabled || undefined}
          onMouseDown={onDragHandleMouseDown}
          className={cn(
            'flex size-[24px] shrink-0 select-none items-center justify-center rounded-[4px]',
            disabled
              ? 'cursor-not-allowed opacity-0'
              : 'cursor-grab hover:bg-black/5 active:cursor-grabbing',
            dragging && 'bg-black/5',
            dragHandleProps?.className,
          )}
        >
          <SIcon name="drag" size={16} color="grey_65" />
        </div>
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-[8px]',
            dragging && 'opacity-(--cmp-listDraggableItem-dragging-content-opacity)',
          )}
        >
          <span
            className={cn(
              'min-w-0 flex-1',
              supportingTextPosition === 'bottom'
                ? 'flex flex-col'
                : 'flex items-center gap-[8px] whitespace-nowrap',
            )}
          >
            <span className="min-w-0 truncate">{titleNode}</span>
            {hasSupportingText && (
              <span
                className={cn(
                  'typo-body-sm-default min-w-0 truncate text-(--sys-color-fg-secondary)',
                  supportingTextPosition === 'right' && 'flex-1',
                )}
              >
                {supportingTextNode}
              </span>
            )}
          </span>
          {trailingNode}
        </div>
      </div>
    );
  },
);

const SDraggableItemImpl = forwardRef<HTMLDivElement, SDraggableItemProps>(function SDraggableItem(
  {
    title,
    supportingText,
    supportingTextPosition = 'right',
    trailing,
    accentStripe = false,
    bordered = false,
    selected = false,
    dragging = false,
    dense = false,
    size = 'sm',
    disabled = false,
    dragOverlay = true,
    dragOverlayOpacity = 0.75,
    dragHandleProps,
    onDragHandleMouseDown,
    className,
    onMouseEnter,
    onMouseLeave,
    ...rest
  },
  ref,
) {
  const [hovered, setHovered] = useState(false);
  const [overlay, setOverlay] = useState<DragOverlayState | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const overlayVisible = overlay != null;

  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    if (!disabled) setHovered(true);
    onMouseEnter?.(event);
  };

  const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    setHovered(false);
    onMouseLeave?.(event);
  };

  const handleDragMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (dragOverlay && rect) {
      setOverlay({
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        rect,
        x: event.clientX,
        y: event.clientY,
      });
    }
    dragHandleProps?.onMouseDown?.(event);
    onDragHandleMouseDown?.(event);
  };

  useEffect(() => {
    if (!overlayVisible) return undefined;

    const handleMouseMove = (event: globalThis.MouseEvent) => {
      setOverlay(current =>
        current
          ? {
              ...current,
              x: event.clientX,
              y: event.clientY,
            }
          : current,
      );
    };

    const handleMouseUp = () => {
      setOverlay(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [overlayVisible]);

  return (
    <>
      <SDraggableItemView
        ref={node => setRefs([rootRef, ref], node)}
        title={title}
        supportingText={supportingText}
        supportingTextPosition={supportingTextPosition}
        trailing={trailing}
        accentStripe={accentStripe}
        bordered={bordered}
        selected={selected}
        dense={dense}
        size={size}
        dragging={dragging || overlayVisible}
        disabled={disabled}
        hovered={hovered}
        dragHandleProps={dragHandleProps}
        onDragHandleMouseDown={handleDragMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={className}
        {...rest}
      />
      {overlay &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed"
            style={{
              left: overlay.x - overlay.offsetX,
              opacity: dragOverlayOpacity,
              top: overlay.y - overlay.offsetY,
              width: overlay.rect.width,
              zIndex: Z_INDEX.drag,
            }}
          >
            <SDraggableItemView
              title={title}
              supportingText={supportingText}
              supportingTextPosition={supportingTextPosition}
              trailing={trailing}
              accentStripe={accentStripe}
              bordered={bordered}
              selected={selected}
              dense={dense}
              size={size}
              dragging
              disabled={disabled}
              hovered
              dragHandleProps={dragHandleProps}
              className={className}
            />
          </div>,
          document.body,
        )}
    </>
  );
});

export const SDraggableItem = withDisplayName(SDraggableItemImpl, 'SDraggableItem');
